
import { useState, FormEvent } from 'react';
import { useCart } from '../../../context/CartContext';

interface CheckoutFormProps {
  onSuccess: (orderRef: string) => void;
}

/* List of US states */
const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
  'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
  'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming'
];

export default function CheckoutForm({ onSuccess }: CheckoutFormProps) {
  const { cartItems, cartTotal, clearCart } = useCart();

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pricing calculations
  const shipping = cartTotal >= 100 ? 0 : 7.99;
  const tax = cartTotal * 0.08;
  const grandTotal = cartTotal + shipping + tax;

  /**
   * Validate required fields and e‑mail format.
   */
  const validate = (form: HTMLFormElement) => {
    const data = new FormData(form);
    const errs: Record<string, string> = {};

    const required = ['firstName','lastName','email','address','city','state','zip'];
    required.forEach((field) => {
      if (!data.get(field)?.toString().trim()) {
        errs[field] = 'This field is required';
      }
    });

    const email = data.get('email')?.toString() ?? '';
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Enter a valid email address';
    }

    return errs;
  };

  /**
   * Handles form submission – validates, builds the request payload,
   * posts to the Readdy endpoint and notifies the parent component.
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // ---- Validation -------------------------------------------------
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({}); // clear previous errors
    setSubmitting(true);

    // ---- Build request payload ---------------------------------------
    const data = new FormData(form);

    const itemsStr = cartItems
      .map((item) =>
        `${item.name}${item.variant ? ` (${item.variant})` : ''}` +
        `${item.size ? ` [${item.size}]` : ''} x${item.quantity} @ $${item.price.toFixed(2)}`
      )
      .join('; ');

    const params = new URLSearchParams();
    params.append('firstName', data.get('firstName')?.toString() ?? '');
    params.append('lastName', data.get('lastName')?.toString() ?? '');
    params.append('email', data.get('email')?.toString() ?? '');
    params.append('phone', data.get('phone')?.toString() ?? '');
    params.append('address', data.get('address')?.toString() ?? '');
    params.append('city', data.get('city')?.toString() ?? '');
    params.append('state', data.get('state')?.toString() ?? '');
    params.append('zip', data.get('zip')?.toString() ?? '');
    params.append('notes', data.get('notes')?.toString() ?? '');
    params.append('orderItems', itemsStr);
    params.append('orderTotal', `$${grandTotal.toFixed(2)}`);

    // ---- Send request ------------------------------------------------
    try {
      const response = await fetch('https://readdy.ai/api/form/d6gccfqn5jm489bu6390', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      // Simulate an order reference
      const ref = 'NM-' + Math.random().toString(36).substring(2, 8).toUpperCase();
      clearCart();
      onSuccess(ref);
    } catch (err) {
      console.error('Checkout submission failed:', err);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to generate input CSS classes based on validation state
  const inputClass = (field: string) =>
    `w-full bg-[#1a1a1a] text-white text-sm px-4 py-3 rounded-lg border transition-colors focus:outline-none ${
      errors[field]
        ? 'border-red-600 focus:border-red-500'
        : 'border-gray-800 focus:border-[#8B0000]'
    }`;

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="checkout-form"
      className="space-y-8"
    >
      {/* Contact Info */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
        <h2 className="text-white text-lg font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
          <span className="w-7 h-7 flex items-center justify-center bg-[#8B0000] rounded-full text-xs font-bold">1</span>
          Contact Information
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
              First Name *
            </label>
            <input name="firstName" type="text" placeholder="Zane" className={inputClass('firstName')} />
            {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
          </div>

          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
              Last Name *
            </label>
            <input name="lastName" type="text" placeholder="Morehouse" className={inputClass('lastName')} />
            {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
          </div>

          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
              Email *
            </label>
            <input name="email" type="email" placeholder="you@example.com" className={inputClass('email')} />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
              Phone
            </label>
            <input name="phone" type="tel" placeholder="+1 (555) 000-0000" className={inputClass('phone')} />
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
        <h2 className="text-white text-lg font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
          <span className="w-7 h-7 flex items-center justify-center bg-[#8B0000] rounded-full text-xs font-bold">2</span>
          Shipping Address
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
              Street Address *
            </label>
            <input name="address" type="text" placeholder="123 Nightmare Lane" className={inputClass('address')} />
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
                City *
              </label>
              <input name="city" type="text" placeholder="Houston" className={inputClass('city')} />
              {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
            </div>

            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
                State *
              </label>
              <select
                name="state"
                defaultValue=""
                className={inputClass('state') + ' cursor-pointer'}
              >
                <option value="" disabled>
                  Select state
                </option>
                {US_STATES.map((s) => (
                  <option key={s} value={s} className="bg-[#1a1a1a]">
                    {s}
                  </option>
                ))}
              </select>
              {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
            </div>

            <div>
              <label className="text-gray-400 text-xs uppercase tracking-wider mb-1.5 block">
                ZIP Code *
              </label>
              <input name="zip" type="text" placeholder="77001" className={inputClass('zip')} />
              {errors.zip && <p className="text-red-500 text-xs mt-1">{errors.zip}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Order Notes */}
      <div className="bg-[#111] border border-gray-800 rounded-xl p-6">
        <h2 className="text-white text-lg font-bold uppercase tracking-wider mb-6 flex items-center gap-2">
          <span className="w-7 h-7 flex items-center justify-center bg-[#8B0000] rounded-full text-xs font-bold">3</span>
          Order Notes{' '}
          <span className="text-gray-600 text-sm font-normal normal-case tracking-normal">
            (optional)
          </span>
        </h2>
        <textarea
          name="notes"
          maxLength={500}
          rows={3}
          placeholder="Special instructions, gift messages, etc."
          className="w-full bg-[#1a1a1a] text-white text-sm px-4 py-3 rounded-lg border border-gray-800 focus:border-[#8B0000] focus:outline-none transition-colors resize-none"
        />
        <p className="text-gray-600 text-xs mt-1">Max 500 characters</p>
      </div>

      {/* Payment Notice */}
      <div className="bg-[#1a1a1a] border border-gray-800 rounded-xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
          <i className="ri-information-line text-[#8B0000] text-2xl"></i>
        </div>
        <div>
          <p className="text-white text-sm font-semibold mb-1">Payment & Fulfillment</p>
          <p className="text-gray-500 text-xs leading-relaxed">
            After submitting your order, our team will contact you via email with payment instructions and shipping details. We accept Venmo, Cash App, PayPal, and major credit cards.
          </p>
        </div>
      </div>

      {/* Submission error */}
      {errors.submit && (
        <p className="text-red-500 text-sm text-center">{errors.submit}</p>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#8B0000] hover:bg-[#6B0000] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-5 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer text-lg uppercase tracking-widest flex items-center justify-center gap-3"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin text-xl"></i>
            Placing Order...
          </>
        ) : (
          <>
            <i className="ri-shield-check-line text-xl"></i>
            {`Place Order — $${grandTotal.toFixed(2)}`}
          </>
        )}
      </button>

      <p className="text-gray-600 text-xs text-center">
        By placing your order you agree to our terms of service. Your information is kept secure and private.
      </p>
    </form>
  );
}
